import React from 'react';
import { Helmet } from 'react-helmet-async';
import BackButton from '../components/BackButton';
import SEO from '../components/SEO';

export default function Confidentialite() {
  return (
    <>
      <SEO 
        title="Politique de Confidentialité | DumaExpertBéton"
        description="Politique de confidentialité et protection des données personnelles de DumaExpertBéton."
        url="https://duma-expert-beton.fr/confidentialite"
      />

      <BackButton />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-slate-100">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Politique de Confidentialité
            </h1>
            <p className="text-lg text-slate-200">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-10 p-6 bg-slate-800/50 rounded-xl border border-slate-700 shadow-lg">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">Introduction</h2>
            <p className="text-slate-100 leading-relaxed mb-4">
              DumaExpertBéton, spécialiste du béton imprimé et décoratif basé à Orange (Vaucluse), 
              s'engage à protéger la vie privée et les données personnelles de ses clients et visiteurs 
              conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
            <p className="text-slate-100 leading-relaxed">
              Cette politique de confidentialité vous informe sur la manière dont nous collectons, 
              utilisons, stockons et protégeons vos données personnelles.
            </p>
          </section>

          {/* Responsable du traitement */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">1. Responsable du Traitement des Données</h2>
            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <p className="text-slate-100"><strong className="text-white">Raison sociale :</strong> DumaExpertBéton</p>
              <p className="text-slate-100"><strong className="text-white">Adresse :</strong> Orange, Vaucluse, France</p>
              <p className="text-slate-100"><strong className="text-white">Email :</strong> contact@duma-expert-beton.fr</p>
              <p className="text-slate-100"><strong className="text-white">Téléphone :</strong> 06 52 59 34 41</p>
            </div>
          </section>

          {/* Données collectées */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">2. Données Personnelles Collectées</h2>
            <p className="text-slate-100 mb-4">
              Nous collectons les données personnelles suivantes uniquement lorsque vous utilisez nos formulaires de contact ou de prise de rendez-vous :
            </p>
            
            <div className="space-y-4">
              <div className="bg-slate-800/30 rounded-lg p-5 border-l-4 border-terracotta-500">
                <h3 className="font-bold text-white mb-2">Via le formulaire de contact :</h3>
                <ul className="list-disc list-inside text-slate-100 space-y-1 ml-4">
                  <li>Nom et prénom</li>
                  <li>Adresse email</li>
                  <li>Numéro de téléphone</li>
                  <li>Localité (ville)</li>
                  <li>Type de service souhaité</li>
                  <li>Surface estimée du projet (optionnel)</li>
                  <li>Message/description du projet</li>
                  <li>Date et heure de soumission du formulaire</li>
                  <li>Consentement RGPD et horodatage</li>
                </ul>
              </div>

              <div className="bg-slate-800/30 rounded-lg p-5 border-l-4 border-clay-500">
                <h3 className="font-bold text-orange-400 mb-2">Via le formulaire de rendez-vous :</h3>
                <ul className="list-disc list-inside text-slate-100 space-y-1 ml-4">
                  <li>Nom et prénom</li>
                  <li>Adresse email</li>
                  <li>Numéro de téléphone</li>
                  <li>Type de service souhaité</li>
                  <li>Date et heure du rendez-vous souhaité</li>
                  <li>Notes complémentaires (optionnel)</li>
                  <li>Date et heure de réservation</li>
                  <li>Consentement RGPD et horodatage</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Finalités du traitement */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">3. Finalités du Traitement</h2>
            <p className="text-slate-100 mb-4">Vos données personnelles sont collectées et traitées pour les finalités suivantes :</p>
            
            <div className="space-y-3">
              {[
                { icon: "📞", text: "Répondre à vos demandes de devis et de renseignements" },
                { icon: "📅", text: "Gérer vos prises de rendez-vous et confirmer les créneaux" },
                { icon: "📧", text: "Vous envoyer des emails de confirmation et de suivi" },
                { icon: "🤝", text: "Assurer le suivi de la relation client et la gestion des projets" },
                { icon: "📊", text: "Améliorer la qualité de nos services" },
                { icon: "⚖️", text: "Respecter nos obligations légales et réglementaires" }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-slate-800/20 p-4 rounded-lg">
                  <span className="text-2xl">{item.icon}</span>
                  <p className="text-slate-100">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Base légale */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">4. Base Légale du Traitement</h2>
            <p className="text-slate-100 mb-4">
              Le traitement de vos données personnelles repose sur les bases légales suivantes :
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-slate-100">
                <span className="text-white font-bold mt-1">✓</span>
                <span><strong>Votre consentement explicite</strong> : En cochant la case RGPD lors de la soumission du formulaire, vous consentez au traitement de vos données personnelles.</span>
              </li>
              <li className="flex items-start gap-3 text-slate-100">
                <span className="text-white font-bold mt-1">✓</span>
                <span><strong>L'exécution d'un contrat</strong> : Le traitement est nécessaire pour répondre à votre demande de devis ou pour gérer votre rendez-vous.</span>
              </li>
              <li className="flex items-start gap-3 text-slate-100">
                <span className="text-white font-bold mt-1">✓</span>
                <span><strong>Notre intérêt légitime</strong> : Gérer la relation client et assurer le bon fonctionnement de notre activité.</span>
              </li>
            </ul>
          </section>

          {/* Destinataires */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">5. Destinataires des Données</h2>
            <p className="text-slate-100 mb-4">
              Vos données personnelles sont destinées exclusivement aux personnes suivantes :
            </p>
            <ul className="list-disc list-inside text-slate-100 space-y-2 ml-4">
              <li>Le personnel autorisé de DumaExpertBéton (gérant, équipe commerciale)</li>
              <li>Notre prestataire d'hébergement web (serveurs sécurisés)</li>
              <li>Notre prestataire d'envoi d'emails (SendGrid, conforme RGPD)</li>
            </ul>
            <div className="mt-4 bg-teal-900/20 border border-teal-700 rounded-lg p-4">
              <p className="text-teal-200">
                <strong>Important :</strong> Nous ne vendons, ne louons ni ne partageons vos données personnelles 
                avec des tiers à des fins commerciales ou marketing.
              </p>
            </div>
          </section>

          {/* Durée de conservation */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">6. Durée de Conservation des Données</h2>
            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <p className="text-slate-100 mb-4">
                Vos données personnelles sont conservées pendant les durées suivantes :
              </p>
              <ul className="space-y-3 text-slate-100">
                <li className="flex items-start gap-3">
                  <span className="text-white font-bold">→</span>
                  <span><strong>Demandes de devis et contacts :</strong> 3 ans à compter de la dernière interaction</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-white font-bold">→</span>
                  <span><strong>Clients ayant signé un contrat :</strong> Durée légale de conservation des documents comptables (10 ans après la fin du contrat)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-white font-bold">→</span>
                  <span><strong>Rendez-vous non confirmés ou annulés :</strong> 1 an</span>
                </li>
              </ul>
              <p className="text-slate-400 text-sm mt-4">
                À l'expiration de ces délais, vos données sont automatiquement supprimées ou anonymisées.
              </p>
            </div>
          </section>

          {/* Droits des personnes */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">7. Vos Droits RGPD</h2>
            <p className="text-slate-100 mb-4">
              Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: "Droit d'accès", desc: "Obtenir une copie de vos données personnelles", icon: "👁️" },
                { title: "Droit de rectification", desc: "Corriger des données inexactes ou incomplètes", icon: "✏️" },
                { title: "Droit à l'effacement", desc: "Demander la suppression de vos données", icon: "🗑️" },
                { title: "Droit d'opposition", desc: "Vous opposer au traitement de vos données", icon: "🚫" },
                { title: "Droit à la limitation", desc: "Limiter le traitement de vos données", icon: "⏸️" },
                { title: "Droit à la portabilité", desc: "Recevoir vos données dans un format structuré", icon: "📦" }
              ].map((right, index) => (
                <div key={index} className="bg-slate-800/30 p-5 rounded-lg border border-slate-700 hover:border-terracotta-500 transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{right.icon}</span>
                    <h3 className="font-bold text-white">{right.title}</h3>
                  </div>
                  <p className="text-slate-400 text-sm">{right.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-gradient-to-r from-terracotta-900/30 to-clay-900/30 border border-terracotta-700 rounded-lg p-6">
              <h3 className="font-bold text-terracotta-300 mb-3 text-lg">Comment exercer vos droits ?</h3>
              <p className="text-slate-100 mb-3">
                Pour exercer l'un de ces droits, vous pouvez nous contacter par :
              </p>
              <ul className="space-y-2 text-slate-100">
                <li className="flex items-center gap-2">
                  <span className="text-white">📧</span>
                  <span><strong>Email :</strong> contact@duma-expert-beton.fr</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-white">📞</span>
                  <span><strong>Téléphone :</strong> 06 52 59 34 41</span>
                </li>
              </ul>
              <p className="text-slate-400 text-sm mt-4">
                Nous vous répondrons dans un délai maximum d'<strong>1 mois</strong> à compter de la réception de votre demande.
              </p>
            </div>
          </section>

          {/* Sécurité */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">8. Sécurité des Données</h2>
            <p className="text-slate-100 mb-4">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre :
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "La destruction accidentelle ou illicite",
                "La perte accidentelle",
                "L'altération des données",
                "La divulgation ou l'accès non autorisé",
                "Le traitement illicite",
                "Toute autre forme de traitement non autorisé"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-slate-800/20 p-4 rounded-lg">
                  <span className="text-red-400 text-lg">🔒</span>
                  <p className="text-slate-100">{item}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-slate-800/40 rounded-lg p-5 border-l-4 border-teal-500">
              <p className="text-slate-100">
                <strong className="text-teal-400">Mesures de sécurité :</strong> Connexions HTTPS sécurisées, 
                chiffrement des données, accès restreint aux données personnelles, sauvegardes régulières, 
                hébergement sécurisé conforme RGPD.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">9. Cookies et Technologies Similaires</h2>
            <p className="text-slate-100 mb-4">
              Notre site web peut utiliser des cookies techniques strictement nécessaires au fonctionnement du site 
              (gestion de session, sécurité). Ces cookies ne nécessitent pas de consentement préalable.
            </p>
            <p className="text-slate-100">
              Si nous utilisons d'autres types de cookies (analytiques, publicitaires), un bandeau de consentement 
              vous sera présenté lors de votre première visite.
            </p>
          </section>

          {/* Modifications */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">10. Modifications de la Politique de Confidentialité</h2>
            <p className="text-slate-100">
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
              Toute modification sera publiée sur cette page avec une date de mise à jour. 
              Nous vous encourageons à consulter régulièrement cette page pour rester informé de nos pratiques en matière de protection des données.
            </p>
          </section>

          {/* Réclamation */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">11. Droit de Réclamation</h2>
            <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-6">
              <p className="text-slate-100 mb-4">
                Si vous estimez que vos droits en matière de protection des données personnelles ne sont pas respectés, 
                vous avez le droit d'introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
              </p>
              <div className="bg-slate-900/50 rounded-lg p-4 space-y-2 text-slate-100">
                <p><strong className="text-amber-400">CNIL</strong></p>
                <p>3 Place de Fontenoy - TSA 80715</p>
                <p>75334 PARIS CEDEX 07</p>
                <p>Téléphone : 01 53 73 22 22</p>
                <p>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">www.cnil.fr</a></p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-r from-terracotta-900/30 via-slate-800/50 to-clay-900/30 border border-terracotta-700 rounded-xl p-8 shadow-xl">
            <h2 className="text-2xl font-bold text-orange-400 mb-4">12. Contact</h2>
            <p className="text-slate-100 mb-6">
              Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
              vous pouvez nous contacter :
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-900/50 rounded-lg p-5 border border-slate-700">
                <h3 className="font-bold text-white mb-3">Par email</h3>
                <a href="mailto:contact@duma-expert-beton.fr" className="text-clay-300 hover:text-clay-200 underline text-lg">
                  contact@duma-expert-beton.fr
                </a>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-5 border border-slate-700">
                <h3 className="font-bold text-white mb-3">Par téléphone</h3>
                <a href="tel:0652593441" className="text-clay-300 hover:text-clay-200 underline text-lg">
                  06 52 59 34 41
                </a>
              </div>
            </div>
          </section>

          {/* Footer note */}
          <div className="mt-12 text-center text-slate-500 text-sm">
            <p>Cette politique de confidentialité a été mise à jour le {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            <p className="mt-2">DumaExpertBéton - Spécialiste du béton imprimé et décoratif | Orange, Vaucluse</p>
          </div>

        </div>
      </div>
    </>
  );
}
